import { client } from "../../amplify-client";

function normalizeChatPk(chatPk) {
  return String(chatPk || "").trim();
}

function getHiddenConversationModel() {
  return client?.models?.UserHiddenConversation;
}

export async function listHiddenConversationsForUser(userId) {
  const entries = await listHiddenConversationEntriesForUser(userId);
  return new Set(entries.map((entry) => entry.chatId));
}

export async function listHiddenConversationEntriesForUser(userId) {
  const normalizedUserId = String(userId || "").trim();
  if (!normalizedUserId) return [];
  const hiddenModel = getHiddenConversationModel();
  if (!hiddenModel || typeof hiddenModel.list !== "function") {
    // Fail-open until backend schema is deployed in this environment.
    return [];
  }

  let nextToken = undefined;
  const entries = [];

  try {
    do {
      const resp = await hiddenModel.list({
        filter: { user_id: { eq: normalizedUserId } },
        limit: 200,
        nextToken,
      });

      const { data, errors } = resp ?? {};
      if (errors?.length) {
        throw new Error(errors.map((e) => e.message).join(", "));
      }

      (data || []).forEach((entry) => {
        const chatId = normalizeChatPk(entry?.chat_id);
        if (!chatId) return;
        entries.push({
          chatId,
          hiddenAt: entry?.hidden_at || null,
        });
      });

      nextToken = resp?.nextToken;
    } while (nextToken);
  } catch (error) {
    console.warn("Hidden conversation lookup failed; defaulting to visible conversations.", error);
    return [];
  }

  entries.sort((a, b) => String(b.hiddenAt || "").localeCompare(String(a.hiddenAt || "")));
  return entries;
}

export async function hideConversationForUser({ userId, chatPk }) {
  const normalizedUserId = String(userId || "").trim();
  const normalizedChatPk = normalizeChatPk(chatPk);
  if (!normalizedUserId || !normalizedChatPk) {
    throw new Error("Missing userId or chatPk for hide conversation.");
  }

  const hiddenModel = getHiddenConversationModel();
  if (!hiddenModel || typeof hiddenModel.get !== "function" || typeof hiddenModel.create !== "function") {
    throw new Error("Conversation hide is unavailable until backend schema updates are deployed.");
  }

  const getResp = await hiddenModel.get({
    user_id: normalizedUserId,
    chat_id: normalizedChatPk,
  });

  if (getResp?.errors?.length) {
    throw new Error(getResp.errors.map((e) => e.message).join(", "));
  }

  if (getResp?.data) {
    return getResp.data;
  }

  const createResp = await hiddenModel.create({
    user_id: normalizedUserId,
    chat_id: normalizedChatPk,
    hidden_at: new Date().toISOString(),
  });

  if (createResp?.errors?.length) {
    throw new Error(createResp.errors.map((e) => e.message).join(", "));
  }

  return createResp?.data ?? null;
}

function isRecordNotFoundErrorMessage(message) {
  const normalized = String(message || "").toLowerCase();
  return (
    normalized.includes("not found") ||
    normalized.includes("does not exist") ||
    normalized.includes("conditionalcheckfailed") ||
    normalized.includes("no item")
  );
}

export async function unhideConversationForUser({ userId, chatPk }) {
  const normalizedUserId = String(userId || "").trim();
  const normalizedChatPk = normalizeChatPk(chatPk);
  if (!normalizedUserId || !normalizedChatPk) {
    throw new Error("Missing userId or chatPk for unhide conversation.");
  }

  const hiddenModel = getHiddenConversationModel();
  if (!hiddenModel || typeof hiddenModel.delete !== "function") {
    throw new Error("Conversation unhide is unavailable until backend schema updates are deployed.");
  }

  const deleteResp = await hiddenModel.delete({
    user_id: normalizedUserId,
    chat_id: normalizedChatPk,
  });

  if (deleteResp?.errors?.length) {
    const messages = deleteResp.errors
      .map((entry) => String(entry?.message || "").trim())
      .filter(Boolean);
    // Idempotent unhide: if the row is already gone (because the same user
    // raced through this path on another tab, or because the row was never
    // created in the first place) we still report success. The caller's
    // post-conditions — chat is no longer in the hidden list — are met.
    const allErrorsAreNotFound =
      messages.length > 0 &&
      messages.every((message) => isRecordNotFoundErrorMessage(message));
    if (!allErrorsAreNotFound) {
      throw new Error(messages.join(", ") || "Failed to unhide conversation.");
    }
  }

  return true;
}
